/**
 * Modo Descoberta / Recomendações (ADITIVO).
 *
 * Deteta pedidos de recomendação/comparação ("qual é o melhor…", "melhores…",
 * "top…", "onde comer…", "recomenda-me…") que NÃO são pedidos de rota.
 * Quando não há sinal claro de recomendação — ou quando o utilizador pede
 * explicitamente rota/itinerário/duração — devolve `null` e todos os fluxos
 * Premium e Pro atuais mantêm-se exatamente iguais.
 */

import { extractDestinationFromPrompt } from "./destination-extraction";
import { CATEGORY_FOCUS_LABELS } from "./category-focus-labels";
import { detectBreakfastIntent } from "./breakfast-intent";
import { detectDishIntent } from "./dish-intent";

export type DiscoveryCategory =
  | "restaurant"
  | "cafe"
  | "bar"
  | "hotel"
  | "beach"
  | "viewpoint"
  | "museum"
  | "nature_park"
  | "water_park"
  | "amusement_park"
  | "attraction"
  | "sweets"
  | "breakfast";

export type DiscoveryRequest = {
  category: DiscoveryCategory;
  /** Termo específico pedido pelo utilizador ("francesinhas"), se existir. */
  specialty: string | null;
  /** Zona/região escrita no pedido ("Norte de Portugal"), se existir. */
  area: string | null;
  /** "perto de mim" / "near me" → usar geolocalização. */
  nearMe: boolean;
  /** Query em inglês para o Places. */
  searchQuery: string;
  /** Produto/prato pedido em texto livre (motor genérico), quando existe. */
  dish?: string | null;
  /** O produto pedido é doçaria. */
  sweetDish?: boolean;
};

const W = "(?<![\\p{L}])";
const Wend = "(?![\\p{L}])";

/** Marcadores de recomendação/superlativo/ranking. */
const DISCOVERY_MARKER = new RegExp(
  `${W}(melhor|melhores|mejor|mejores|migliore|migliori|meilleur|meilleurs|meilleure|meilleures|beste|besten|best|top\\s*\\d*|ranking|onde\\s+comer|onde\\s+jantar|onde\\s+almo[cç]ar|onde\\s+(?:posso\\s+|se\\s+pode\\s+)?(?:comprar|provar|encontrar|degustar)|(?:posso|podemos)\\s+(?:comprar|provar|encontrar|degustar)|where\\s+to\\s+eat|where\\s+(?:can\\s+i\\s+|to\\s+)(?:buy|try|taste|find)|d[oó]nde\\s+comer|d[oó]nde\\s+(?:puedo\\s+)?(?:comprar|probar)|o[uù]\\s+(?:puis-je\\s+)?(?:acheter|go[uû]ter)|dove\\s+(?:posso\\s+)?(?:comprare|assaggiare)|wo\\s+(?:kann\\s+ich\\s+)?(?:kaufen|probieren)|recomenda\\w*|recomienda\\w*|recommend\\w*|sugere\\w*(?:\\s+(?:me\\s+)?(?:os|as)?)?)${Wend}`,
  "iu",
);

/**
 * Pedidos que continuam a ser rota/itinerário: o modo descoberta NUNCA liga.
 */
const ROUTE_MARKER = new RegExp(
  `${W}(rota|rotas|percurso|percursos|itiner[aá]rio\\w*|roteiro\\w*|route|routes|itinerar\\w*|circuito|tour\\b|road\\s?trip)${Wend}`,
  "iu",
);

const DURATION_MARKER = new RegExp(
  `(${W}(tenho|temos|tengo|ho\\b|j['’]ai|ich\\s+habe|i\\s+have|we\\s+have)${Wend}[^.?!]{0,20}\\d)|(\\d+\\s*(h|hrs?|horas?|hours?|dias?|days?|giorni?|jours?|d[ií]as?|tage?)${Wend})|(${W}(uma|um|meio|meia)\\s+(dia|semana|tarde|manh[ãa])${Wend})`,
  "iu",
);

const VISIT_MARKER = new RegExp(
  `${W}(quero\\s+visitar|vou\\s+a|vou\\s+ao|vou\\s+para|quiero\\s+visitar|voglio\\s+visitare|je\\s+vais\\s+[àa]|i['’]?m\\s+going\\s+to|planear\\s+viagem|plan\\s+a\\s+trip)${Wend}`,
  "iu",
);

/** Categoria única explícita ("só restaurantes") — fluxo atual, não descoberta. */
const EXCLUSIVE_MARKER = new RegExp(
  `${W}(s[oó]|apenas|somente|unicamente|only|just|nur|solamente|seulement|uniquement)${Wend}`,
  "iu",
);

const NEAR_ME = new RegExp(
  `(perto\\s+de\\s+mim|perto\\s+daqui|aqui\\s+perto|cerca\\s+de\\s+m[ií]|near\\s+me|nearby|around\\s+me|pr[eè]s\\s+de\\s+moi|in\\s+der\\s+n[aä]he|vicino\\s+a\\s+me)`,
  "iu",
);

type CategoryPattern = { id: DiscoveryCategory; re: RegExp; query: string };

const CATEGORY_PATTERNS: CategoryPattern[] = [
  {
    id: "water_park",
    re: new RegExp(`${W}(parques?\\s+aqu[aá]ticos?|water\\s?parks?|aquaparks?|parques?\\s+acu[aá]ticos?)${Wend}`, "iu"),
    query: "water park",
  },
  {
    id: "amusement_park",
    re: new RegExp(`${W}(parques?\\s+de\\s+divers[oõ]es?|parques?\\s+tem[aá]ticos?|amusement\\s+parks?|theme\\s+parks?)${Wend}`, "iu"),
    query: "amusement park",
  },
  {
    id: "nature_park",
    re: new RegExp(
      `${W}(parques?\\s+naturais?|parques?\\s+nacionais?|reservas?\\s+naturais?|nature\\s+parks?|national\\s+parks?|trilhos?|hiking\\s+trails?)${Wend}`,
      "iu",
    ),
    query: "nature park",
  },
  {
    id: "beach",
    re: new RegExp(`${W}(praias?|beaches?|beach|playas?|plages?|str[aä]nde?)${Wend}`, "iu"),
    query: "beach",
  },
  {
    id: "museum",
    re: new RegExp(`${W}(museus?|museums?|mus[eé]es?|museos?|galerias?\\s+de\\s+arte|art\\s+galler\\w+)${Wend}`, "iu"),
    query: "museum",
  },
  {
    id: "viewpoint",
    re: new RegExp(`${W}(miradour\\w+|viewpoints?|overlooks?|miradores?|panoram\\w+)${Wend}`, "iu"),
    query: "scenic viewpoint",
  },
  {
    id: "hotel",
    re: new RegExp(`${W}(hot[eé]is|hotel|hotels|pousadas?|alojamentos?|hostels?|resorts?|guest\\s?houses?)${Wend}`, "iu"),
    query: "hotel",
  },
  {
    id: "cafe",
    re: new RegExp(`${W}(caf[eé]s?|pastelarias?|padarias?|bakeries|bakery|coffee\\s+shops?|confeitarias?)${Wend}`, "iu"),
    query: "cafe pastry shop",
  },
  {
    id: "bar",
    re: new RegExp(`${W}(bares|bar\\b|pubs?|discotecas?|nightlife|rooftop\\s+bars?|cocktail\\s+bars?)${Wend}`, "iu"),
    query: "bar",
  },
  {
    id: "restaurant",
    re: new RegExp(
      `${W}(restaurantes?|restaurants?|tascas?|marisqueiras?|churrasqueiras?|onde\\s+comer|onde\\s+jantar|onde\\s+almo[cç]ar|where\\s+to\\s+eat|places?\\s+to\\s+eat|comer)${Wend}`,
      "iu",
    ),
    query: "restaurant",
  },
  {
    id: "sweets",
    re: new RegExp(
      `${W}(doces?\\s+(?:regionais?|regional(?:es)?|t[ií]pic[oa]s?|tradicionais?|tradicional(?:es)?|conventuais?|conventual)|do[cç]aria|pastelarias?|padarias?\\s+(?:t[ií]picas?|tradicionais?)|confeitarias?|sweets?\\s+shop|traditional\\s+sweets?|pastry\\s+shop|patisserie|dolci\\s+t[ií]pici|pasteles?\\s+t[ií]picos)${Wend}`,
      "iu",
    ),
    query: "traditional sweets pastry shop",
  },
  {
    id: "attraction",
    re: new RegExp(
      `${W}(atra[cç][oõ]es|atra[cç][aã]o|attractions?|coisas?\\s+para\\s+ver|things?\\s+to\\s+do|pontos?\\s+tur[ií]sticos?|sights?)${Wend}`,
      "iu",
    ),
    query: "tourist attraction",
  },
];

const AREA_LEAD = "(?:d[oa]s?|de|em|n[oa]s?|in|at|near|around|perto\\s+de|dans|en)";

function clean(value: string): string {
  return value
    .replace(/[!?.,;:]+\s*$/u, "")
    .replace(/\s+/gu, " ")
    .trim();
}

/** Palavras de intenção que nunca fazem parte de uma zona. */
const TRAILING_INTENT = new RegExp(
  `\\s+(?:${["diz", "diz-me", "me", "por\\s+favor", "please", "obrigado", "sff"].join("|")})$`,
  "iu",
);

function extractSpecialtyAndArea(tail: string): { specialty: string | null; area: string | null } {
  const t = clean(tail);
  if (!t) return { specialty: null, area: null };

  // "de francesinhas do Norte de Portugal" → especialidade + zona
  const both = new RegExp(
    `^(?:de|of|com|com\\s+as|para|with)\\s+([\\p{L}\\p{M}'’-]+(?:\\s+[\\p{L}\\p{M}'’-]+){0,2}?)\\s+${AREA_LEAD}\\s+(.+)$`,
    "iu",
  ).exec(t);
  if (both) return { specialty: clean(both[1]), area: clean(both[2]) };

  // "de francesinhas" (sem zona)
  const specialtyOnly = new RegExp(`^(?:de|of|com|para|with)\\s+([\\p{L}\\p{M}'’\\s-]{3,40})$`, "iu").exec(t);
  if (specialtyOnly) return { specialty: clean(specialtyOnly[1]), area: null };

  // "no Porto" / "in Lisbon"
  const areaOnly = new RegExp(`^${AREA_LEAD}\\s+(.+)$`, "iu").exec(t);
  if (areaOnly) return { specialty: null, area: clean(areaOnly[1]) };

  return { specialty: null, area: null };
}

/**
 * Deteta um pedido de descoberta. Devolve `null` sempre que o pedido seja (ou
 * possa ser) um pedido de rota/itinerário.
 */
export function detectDiscoveryRequest(text: string): DiscoveryRequest | null {
  const raw = (text ?? "").trim();
  if (!raw) return null;

  // Pequeno-almoço/brunch: sem rota explícita e sem duração é sempre
  // Descoberta por zonas — mesmo quando a pessoa escreve "só".
  const breakfast = detectBreakfastIntent(raw);
  if (breakfast && breakfast.mode === "discovery") {
    const nearMeNow = NEAR_ME.test(raw);
    const area = nearMeNow ? null : (extractDestinationFromPrompt(raw).city ?? null);
    return {
      category: "breakfast",
      specialty: null,
      area,
      nearMe: nearMeNow,
      searchQuery: breakfast.kind === "brunch" ? "brunch restaurant cafe" : "breakfast cafe bakery",
    };
  }

  if (!DISCOVERY_MARKER.test(raw)) return null;
  if (ROUTE_MARKER.test(raw)) return null;
  if (DURATION_MARKER.test(raw)) return null;

  const match = CATEGORY_PATTERNS.find(({ re }) => re.test(raw));
  if (!match) {
    // Motor genérico: "melhor <prato/doce> de <zona>" para qualquer produto e
    // qualquer zona do mundo, sem catálogo e sem lista de categorias.
    const dish = detectDishIntent(raw);
    if (!dish) return null;
    if (VISIT_MARKER.test(raw)) return null;
    return {
      category: dish.sweet ? "sweets" : "restaurant",
      specialty: dish.dish,
      area: dish.nearMe ? null : (dish.area ?? extractDestinationFromPrompt(raw).city ?? null),
      nearMe: dish.nearMe,
      searchQuery: dish.sweet ? `${dish.dish} pastry shop bakery` : `${dish.dish} restaurant`,
      dish: dish.dish,
      sweetDish: dish.sweet,
    };
  }


  const hasExclusive = EXCLUSIVE_MARKER.test(raw);
  const isSweets = match.id === "sweets";

  // Exclusive marker only allowed for sweets recommendations.
  if (hasExclusive && !isSweets) return null;

  // Visit marker blocks discovery except for exclusive sweets recommendations.
  if (VISIT_MARKER.test(raw) && !(isSweets && hasExclusive)) return null;

  const nearMe = NEAR_ME.test(raw);
  const catMatch = match.re.exec(raw)!;
  let tail = raw.slice((catMatch.index ?? 0) + catMatch[0].length);
  if (nearMe) tail = tail.replace(NEAR_ME, " ");
  tail = clean(tail).replace(TRAILING_INTENT, "");

  let { specialty, area } = extractSpecialtyAndArea(tail);

  // Cidade escrita no texto ("… de Viana do Castelo") vence a zona inferida
  // quando o nome composto foi partido entre especialidade e zona, ou quando
  // não houve zona nenhuma. Pedidos com especialidade real ("francesinhas do
  // Norte de Portugal") mantêm-se exatamente como estão.
  const promptCity = nearMe ? null : (extractDestinationFromPrompt(raw).city ?? null);
  if (promptCity) {
    const norm = (v: string) => v.normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase();
    const city = norm(promptCity);
    // Especialidade em minúsculas ("francesinhas") é mesmo um produto: nunca
    // é parte de um nome de cidade partido ao meio ("Viana" + "do Castelo").
    const splitName =
      Boolean(area) && city.includes(norm(area!)) && (!specialty || city.includes(norm(specialty!)));
    if (!area || splitName) {
      if (specialty && city.includes(norm(specialty))) specialty = null;
      area = promptCity;
    }
  }

  const searchQuery = specialty
    ? `${specialty} ${match.query}`.trim()
    : match.query;

  return {
    category: match.id,
    specialty,
    area: nearMe ? null : area,
    nearMe,
    searchQuery,
    dish: specialty,
    sweetDish: match.id === "sweets",
  };
}

const CATEGORY_LABEL: Record<DiscoveryCategory, Record<string, string>> = CATEGORY_FOCUS_LABELS;

const BEST_WORD: Record<string, string> = {
  pt: "Melhores", en: "Best", es: "Mejores", fr: "Meilleurs", it: "Migliori", de: "Beste",
};

const NEAR_ME_WORD: Record<string, string> = {
  pt: "perto de ti", en: "near you", es: "cerca de ti", fr: "près de toi", it: "vicino a te", de: "in deiner Nähe",
};

const IN_WORD: Record<string, string> = {
  pt: "em", en: "in", es: "en", fr: "à", it: "a", de: "in",
};

/** Título honesto do ranking: "Melhores francesinhas no Norte de Portugal". */
export function discoveryTitle(request: DiscoveryRequest, lang = "pt"): string {
  const l = CATEGORY_LABEL[request.category][lang] ? lang : "en";
  const subject = request.specialty ? request.specialty : CATEGORY_LABEL[request.category][l];
  const best = BEST_WORD[l] ?? BEST_WORD.en;
  if (request.nearMe) return `${best} ${subject} ${NEAR_ME_WORD[l] ?? NEAR_ME_WORD.en}`;
  if (request.area) return `${best} ${subject} ${IN_WORD[l] ?? IN_WORD.en} ${request.area}`;
  return `${best} ${subject}`;
}

/** Nota de transparência: nunca dizemos "o melhor absoluto". */
export function discoveryDisclaimer(lang = "pt"): string {
  switch (lang) {
    case "en":
      return "Best options by rating, number of reviews, relevance to your request and quality of the available data.";
    case "es":
      return "Mejores opciones según valoración, número de reseñas, relevancia y calidad de los datos disponibles.";
    case "fr":
      return "Meilleures options selon la note, le nombre d'avis, la pertinence et la qualité des données disponibles.";
    case "it":
      return "Migliori opzioni per valutazione, numero di recensioni, pertinenza e qualità dei dati disponibili.";
    case "de":
      return "Beste Optionen nach Bewertung, Anzahl der Rezensionen, Relevanz und Datenqualität.";
    default:
      return "Melhores opções segundo classificação, número de avaliações, relevância para o pedido e qualidade dos dados disponíveis.";
  }
}
